import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import CircleText from '../../components/CircleText';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import NoInfoMessage from '../../components/NoInfoMessage';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { TrainingModuleDashboardTrainingModule, TrainingModuleReorderCommand } from '../../api/models';

const ContentTrainingModule: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Module Id
	const [moduleId, setModuleId] = React.useState<string>('');

	// State to store Module title
	const [moduleTitle, setModuleTitle] = React.useState<string>('');

	// Set up data state
	const [trainingModulesData, setTrainingModulesData] = React.useState<TrainingModuleDashboardTrainingModule[]>([]);

	const getTrainingModulesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getTrainingModuleDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setTrainingModulesData(result);
		}
	};

	React.useEffect(() => {
		getTrainingModulesData();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(trainingModulesData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setTrainingModulesData(items);

		const data: TrainingModuleReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postTrainingModuleReorder(data);
	};

	const removeHandler = async (item: TrainingModuleDashboardTrainingModule) => {
		setModuleId(item.id || '');

		setModuleTitle(item.title || '');

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="content-training-module-edit">
										<PlusIcon className="icon-link" />
										Add new
									</Link>
								</div>

								<section className="info-section mtop-0">
									{trainingModulesData && trainingModulesData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="trainingModulesItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{trainingModulesData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row">
																				<div className="col-auto pdl-0 pdr-0">
																					<CircleText text={index + 1} />
																				</div>

																				<div className="col-md-auto full-width">
																					<div className="row detail-info">
																						<div className="col-10 col-md-6 detail-name">
																							{item.title}

																							<p className="text-subtitle">{item.subtitle}</p>
																						</div>

																						<div className="col-md-6 d-none d-md-block text-category-detail">
																							Child Safe Standard
																							<div className="text-subtitle">
																								{item && item.standards && item.standards.length > 1 && (
																									<span>Multiple selected</span>
																								)}

																								{item && item.standards && item.standards.length === 1 && (
																									<span>{item.standards[0]}</span>
																								)}
																							</div>
																						</div>
																					</div>

																					<div className="d-block d-sm-none">
																						<Link
																							className="btn-text remove link "
																							to={`/content-training-module-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="col-auto pdr-0 link-group content-training-module-page">
																					<div className="d-none d-sm-flex">
																						<Link
																							className="btn-text remove link "
																							to={`/content-training-module-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>

																					<div className="drag-icon-container">
																						<DisplayDragIcon index={index} array={trainingModulesData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<NoInfoMessage name="Training modules" />
									)}
								</section>

								<ScrollToTopButton extraClass="mtop-24" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove module">
				<RemoveModal
					trainingModulesData={trainingModulesData}
					setTrainingModulesData={setTrainingModulesData}
					getTrainingModulesData={getTrainingModulesData}
					moduleId={moduleId}
					moduleTitle={moduleTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentTrainingModule;
